import React, { useEffect, useMemo, useRef } from 'react';
import { drawMap, MapCamera, MapRoom, prepareMap, projectMapX } from './mapDesign';
import { SpatialMap } from './protocol';
import { useFocus } from '../../contexts/FocusContext';
import { signalUserActivity } from '../../hooks/useIdleTimeout';
import { sampleSnapshot, TimedSpatialSnapshot } from './interpolation';
import './LevelOne.css';
import { playingMiniGame } from '../UnityGame/miniGameActivity';
import { ATLAS_ROOT, VenueAtlas } from './atlas';
import { createNavigator } from './mapNavigation';
import { drawAtlasWayfinding } from './atlasWayfinding';

interface Props {
  map: SpatialMap;
  snapshotHistory: React.MutableRefObject<TimedSpatialSnapshot[]>;
  onMove: (x: number, z: number, sprint: boolean) => void;
  faceDistance: number;
  rooms?: MapRoom[];
  atlas?: VenueAtlas;
  zoom?: number;
  overview?: boolean;
}

/** One canvas, capped DPR, no per-player DOM or extra video decoders. */
export default function LevelOneCanvas({ map, snapshotHistory, onMove, faceDistance, rooms = [], atlas, zoom = 1, overview = false }: Props) {
  const geometry = useMemo(() => prepareMap(map), [map]);
  const navigate = useMemo(() => createNavigator(map), [map]);
  const roomsRef = useRef(rooms); roomsRef.current = rooms;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const moveRef = useRef(onMove);
  moveRef.current = onMove;
  const { hasActiveOverlay } = useFocus();
  const blocked = useRef(hasActiveOverlay);
  blocked.current = hasActiveOverlay;
  const touch = useRef({ x: 0, z: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { alpha: false });
    if (!canvas || !ctx) return;
    const keys = new Set<string>();
    const badges = new Map<string, HTMLImageElement>();
    let playerHitTargets: { userId: string; displayName: string; x: number; y: number }[] = [];
    let frame = 0;
    let lastDraw = 0;
    let lastLayout = 0;
    let width = 1;
    let height = 1;
    let staticLayer: { canvas: HTMLCanvasElement; camera: MapCamera; key: string; displayScale: number } | undefined;
    const images = [new Image(), new Image()];
    if (atlas) images.forEach((image, i) => { image.src = `${ATLAS_ROOT}/${i ? 'upper' : 'ground'}.webp`; });
    let route: { x: number; y: number; z: number }[] = [];
    let lastCamera: MapCamera | undefined;
    let lastProgress = 0;
    let progressPosition = { x: Infinity, z: Infinity };
    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const mobile = window.matchMedia('(max-width: 640px)').matches;
      const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1 : 1.25);
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();
    let touchPointer: number | undefined;
    let touchOrigin = { x: 0, y: 0 };
    const clear = () => { keys.clear(); route = []; touch.current = { x: 0, z: 0 }; moveRef.current(0, 0, false); };
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') return;
      touchPointer = event.pointerId;
      touchOrigin = { x: event.clientX, y: event.clientY };
      canvas.setPointerCapture(event.pointerId);
      signalUserActivity();
    };
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== touchPointer) return;
      const dx = event.clientX - touchOrigin.x;
      const dy = event.clientY - touchOrigin.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      const strength = Math.min(1, distance / 42);
      touch.current = { x: dx / distance * strength, z: dy / distance * strength };
    };
    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerId !== touchPointer) return;
      touchPointer = undefined;
      touch.current = { x: 0, z: 0 };
    };
    const onClick = (event: MouseEvent) => {
      if (blocked.current) return;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const player = playerHitTargets.find(target => Math.hypot(target.x - x, target.y - y) <= 32);
      if (!player) {
        const state = sampleSnapshot(snapshotHistory.current, performance.now());
        const self = state.players.find(p => p.userId === state.selfId);
        if (atlas && lastCamera && self) {
          route = navigate(self.position, { x: lastCamera.x - (x - width / 2) / lastCamera.scale, y: self.position.y, z: lastCamera.z + (y - height / 2) / lastCamera.scale });
          lastProgress = performance.now(); progressPosition = { ...self.position };
          signalUserActivity();
        }
        return;
      }
      window.dispatchEvent(new CustomEvent('PlayerClicked', {
        detail: JSON.stringify({ userId: player.userId, displayName: player.displayName, mouseButton: 'Left' }),
      }));
    };
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (blocked.current || (target instanceof Element && target.closest('input,textarea,select,[contenteditable="true"],[role="dialog"]'))) {
        clear(); return;
      }
      if (event.code === 'Escape') { clear(); return; }
      if (!['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'ShiftLeft', 'ShiftRight'].includes(event.code)) return;
      event.preventDefault();
      if (event.type === 'keydown') { keys.add(event.code); signalUserActivity(); }
      else keys.delete(event.code);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', clear);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerEnd);
    canvas.addEventListener('pointercancel', onPointerEnd);
    canvas.addEventListener('click', onClick);
    const draw = (time: number) => {
      frame = requestAnimationFrame(draw);
      if (document.hidden || time - lastDraw < 1000 / 30) return;
      lastDraw = time;
      if (blocked.current) clear();
      let x = Number(keys.has('KeyD') || keys.has('ArrowRight')) - Number(keys.has('KeyA') || keys.has('ArrowLeft')) + touch.current.x;
      let z = Number(keys.has('KeyS') || keys.has('ArrowDown')) - Number(keys.has('KeyW') || keys.has('ArrowUp')) + touch.current.z;
      const state = sampleSnapshot(snapshotHistory.current, performance.now());
      const self = state.players.find(p => p.userId === state.selfId);
      if (x || z) route = [];
      if (self && route.length) {
        while (route.length && Math.hypot(route[0].x - self.position.x, route[0].z - self.position.z) < .35) route.shift();
        if (Math.hypot(self.position.x - progressPosition.x, self.position.z - progressPosition.z) > .15) {
          lastProgress = time; progressPosition = { ...self.position };
        }
        // Server remains authoritative; stop if a door/access barrier blocks the path.
        if (time - lastProgress > 1500) route = [];
        if (route.length) {
          const dx = route[0].x - self.position.x, dz = route[0].z - self.position.z;
          const length = Math.max(.01, Math.hypot(dx, dz));
          x = -dx / length; z = dz / length;
        }
      }
      const magnitude = Math.max(1, Math.hypot(x, z));
      // Mirror horizontal input with the map so right remains right locally and
      // resolves to the same authored-world direction observed by Level 2 users.
      moveRef.current(-x / magnitude, z / magnitude, keys.has('ShiftLeft') || keys.has('ShiftRight'));
      const bounds = geometry.bounds;
      // A close follow camera makes movement readable even across large venues.
      const scale = atlas ? (overview ? Math.min(width / 120, height / 120) : 13) * zoom : Math.max(13, Math.min(20, width / 70));
      const position = self?.position || { x: bounds ? (bounds.minX + bounds.maxX) / 2 : 0, y: 0, z: bounds ? (bounds.minZ + bounds.maxZ) / 2 : 0 };
      const camera = { ...position, ...(atlas && overview ? { x: 0, z: -22 } : {}), scale, width, height };
      lastCamera = camera;
      const sx = (n: number) => projectMapX(n, camera);
      const sy = (n: number) => height / 2 + (n - camera.z) * scale;
      ctx.fillStyle = atlas ? '#0b1621' : '#b9d8a6'; ctx.fillRect(0, 0, width, height);
      const background = images[position.y > 4 ? 1 : 0];
      if (atlas && background.complete && background.naturalWidth) {
        ctx.drawImage(background, sx(atlas.maxX), sy(atlas.minZ), (atlas.maxX - atlas.minX) * scale, (atlas.maxZ - atlas.minZ) * scale);
        ctx.font = `600 ${width < 480 ? 9 : 12}px system-ui`; ctx.textAlign = 'center';
        roomsRef.current.forEach(room => {
          const p = room.spawn_position?.replace(/[()]/g, '').split(',').map(Number);
          if (!p || p.length !== 3 || Math.abs(p[1] - position.y) > 3) return;
          const fullLabel = room.name || room.room_name || room.room_id;
          const label = width < 480 ? fullLabel.replace('Breakout Room', 'Room').replace('Breakout ', 'Room ').replace('Call Room', 'Call') : fullLabel;
          const w = ctx.measureText(label).width + 20;
          ctx.fillStyle = '#102735'; ctx.fillRect(sx(p[0]) - w / 2, sy(p[2]) - 13, w, 26);
          ctx.fillStyle = '#f0e6c8'; ctx.fillText(label, sx(p[0]), sy(p[2]) + 4);
        });
      } else if (bounds) {
        // Rasterize immutable venue geometry once. During movement only this
        // bitmap's offset changes; avatars remain on the live canvas layer.
        const floorY = Math.round(position.y / 2) * 2;
        const roomKey = roomsRef.current.map(room => `${room.room_id}:${room.name || room.room_name || ''}:${room.spawn_position || ''}`).join('|');
        const worldWidth = bounds.maxX - bounds.minX + 24;
        const worldHeight = bounds.maxZ - bounds.minZ + 24;
        const requestedWidth = worldWidth * scale;
        const requestedHeight = worldHeight * scale;
        const rasterRatio = Math.min(1, 3072 / requestedWidth, 3072 / requestedHeight, Math.sqrt(6000000 / (requestedWidth * requestedHeight)));
        const rasterScale = scale * rasterRatio;
        const layerWidth = Math.ceil(worldWidth * rasterScale);
        const layerHeight = Math.ceil(worldHeight * rasterScale);
        const displayScale = scale / rasterScale;
        const key = `${layerWidth}:${layerHeight}:${rasterScale.toFixed(3)}:${floorY}:${roomKey}`;
        if (!staticLayer || staticLayer.key !== key) {
          const layer = document.createElement('canvas');
          layer.width = Math.max(1, layerWidth); layer.height = Math.max(1, layerHeight);
          const layerCamera = {
            x: (bounds.minX + bounds.maxX) / 2,
            z: (bounds.minZ + bounds.maxZ) / 2,
            y: floorY,
            scale: rasterScale,
            width: layer.width,
            height: layer.height,
          };
          const layerContext = layer.getContext('2d', { alpha: false });
          if (layerContext) drawMap(layerContext, geometry, layerCamera, roomsRef.current);
          staticLayer = { canvas: layer, camera: layerCamera, key, displayScale };
        }
        const displayWidth = staticLayer.canvas.width * staticLayer.displayScale;
        const displayHeight = staticLayer.canvas.height * staticLayer.displayScale;
        ctx.drawImage(
          staticLayer.canvas,
          projectMapX(staticLayer.camera.x, camera) - displayWidth / 2,
          sy(staticLayer.camera.z) - displayHeight / 2,
          displayWidth,
          displayHeight,
        );
      }
      if (atlas) drawAtlasWayfinding(ctx, camera);
      if (route.length) {
        const target = route[route.length - 1];
        ctx.strokeStyle = '#f4d391'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(sx(target.x), sy(target.z), 8, 0, Math.PI * 2); ctx.stroke();
      }
      if (time - lastLayout > 250) window.dispatchEvent(new CustomEvent('LevelOneMapCamera', { detail: camera }));
      if (!self) {
        ctx.fillStyle = '#173651'; ctx.font = '600 15px system-ui'; ctx.textAlign = 'center';
        ctx.fillText('Waiting for your position…', width / 2, height / 2);
        lastLayout = time;
        return;
      }
      const sources = (window as any).__vvFaceVideoSources as Map<string, { video: HTMLVideoElement }> | undefined;
      const faces: { userId: string; vx: number; vy: number; vz: number; visible: boolean }[] = [];
      const nextPlayerHitTargets: typeof playerHitTargets = [];
      const onlineIds = new Set(state.players.map(player => player.userId));
      for (const player of state.players) {
        const px = sx(player.position.x), py = sy(player.position.z);
        if (px < -80 || py < -60 || px > width + 80 || py > height + 60) continue;
        const distance = Math.hypot(player.position.x - self.position.x, player.position.y - self.position.y, player.position.z - self.position.z);
        const sameFloor = Math.abs(player.position.y - self.position.y) < 2;
        const own = player.userId === state.selfId;
        const game = playingMiniGame(player.userId, onlineIds);
        if (game) {
          const bob = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : Math.sin(performance.now() / 330) * 3;
          ctx.save(); ctx.translate(px, py - 48 + bob);
          ctx.fillStyle = '#142337'; ctx.fillRect(-13, -7, 26, 14);
          ctx.strokeStyle = game === 'chess' ? '#c4a5ff' : '#67e8f9'; ctx.lineWidth = 2; ctx.strokeRect(-13, -7, 26, 14);
          ctx.fillStyle = ctx.strokeStyle; ctx.fillRect(-9, -1, 7, 2); ctx.fillRect(-6, -4, 2, 8);
          ctx.fillRect(5, 0, 3, 3); ctx.fillRect(9, -4, 3, 3); ctx.restore();
        }
        nextPlayerHitTargets.push({ userId: player.userId, displayName: player.displayName, x: px, y: py });
        ctx.globalAlpha = sameFloor ? 1 : 0.3;
        ctx.beginPath(); ctx.arc(px, py, 19, 0, Math.PI * 2);
        ctx.fillStyle = own ? '#ffd073' : '#a8d5e6'; ctx.fill();
        ctx.strokeStyle = own ? '#fff7da' : '#173651'; ctx.lineWidth = 3; ctx.stroke();
        const video = sources?.get(player.userId.toLowerCase())?.video;
        if (sameFloor && distance <= faceDistance) {
          faces.push({ userId: player.userId, vx: px / width, vy: 1 - py / height, vz: Math.max(0.01, distance), visible: true });
          if (video && video.readyState >= 2 && video.videoWidth && video.videoHeight) {
            const side = Math.min(video.videoWidth, video.videoHeight);
            ctx.save(); ctx.clip();
            ctx.drawImage(video, (video.videoWidth - side) / 2, (video.videoHeight - side) / 2, side, side, px - 18, py - 18, 36, 36);
            ctx.restore();
          }
        }
        if (!video || video.readyState < 2 || !sameFloor || distance > faceDistance) {
          ctx.fillStyle = '#0c1530'; ctx.font = 'bold 14px system-ui'; ctx.textAlign = 'center';
          ctx.fillText(player.displayName.slice(0, 2).toUpperCase(), px, py + 5);
        }
        ctx.font = '600 13px system-ui'; ctx.textAlign = 'center'; ctx.fillStyle = '#fff';
        ctx.shadowColor = '#000'; ctx.shadowBlur = 5;
        ctx.fillText(player.displayName + (sameFloor ? '' : player.position.y > self.position.y ? ' ↑' : ' ↓'), px, py - 29);
        if (player.badgeUrl) {
          let badge = badges.get(player.badgeUrl);
          if (!badge && badges.size < 48) { badge = new Image(); badge.src = player.badgeUrl; badges.set(player.badgeUrl, badge); }
          if (badge?.complete && badge.naturalWidth) ctx.drawImage(badge, px - ctx.measureText(player.displayName).width / 2 - 24, py - 44, 18, 18);
        }
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
      playerHitTargets = nextPlayerHitTargets;
      if (time - lastLayout > 250) {
        lastLayout = time;
        window.dispatchEvent(new CustomEvent('VideoFaceLayout', { detail: JSON.stringify({ faces }) }));
      }
    };
    frame = requestAnimationFrame(draw);
    return () => {
      clear(); cancelAnimationFrame(frame); observer.disconnect();
      badges.clear(); staticLayer = undefined;
      window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', clear); document.removeEventListener('visibilitychange', clear);
      canvas.removeEventListener('pointerdown', onPointerDown); canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerEnd); canvas.removeEventListener('pointercancel', onPointerEnd);
      canvas.removeEventListener('click', onClick);
      window.dispatchEvent(new CustomEvent('VideoFaceLayout', { detail: JSON.stringify({ faces: [] }) }));
    };
  }, [geometry, navigate, snapshotHistory, faceDistance, atlas, zoom, overview]);

  return <>
    <canvas ref={canvasRef} data-world-surface="true" aria-label="2D venue map. Use WASD, arrow keys, or click a walkway to walk." tabIndex={0} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', outline: 'none', touchAction: 'none' }} />
  </>;
}
